<template>
  <section class="flex flex-col md:flex-row min-h-screen">
    <!-- Language Switcher -->
    <div class="absolute top-4 right-4 z-50 flex items-center">
      <span class="mr-2 text-sm text-white">{{ t('provider_login.language') }}</span>
      <select
        v-model="currentLocale"
        class="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1 text-sm"
        @change="switchLanguage"
      >
        <option value="en-US">English</option>
        <option value="zh-Hans">简体中文</option>
        <option value="zh-Hant">繁體中文</option>
      </select>
    </div>

    <!-- LEFT PANEL -->
    <div
      class="relative flex-1 p-8 sm:p-12 lg:p-16 text-white bg-cover bg-center bg-no-repeat bg-blend-multiply"
      :style="leftPanelStyle"
    >
      <div class="relative z-10 max-w-md">
        <NuxtLink :to="localePath('/')" class="flex items-center mb-8">
          <img
            class="w-10 h-10 mr-3"
            src="~assets/img/HYS-logo-icon-06.png"
            alt="Site Logo"
          />
          <span class="text-2xl font-bold">{{ t('provider_login.site_name') }}</span>
        </NuxtLink>
        <h2 class="text-3xl md:text-4xl font-extrabold mb-4">
          {{ t('provider_login.page_title') }}
        </h2>
        <p class="text-sm md:text-base leading-relaxed">
          {{ t('provider_login.subtitle') }}<br />
          {{ t('provider_login.subtitle_register') }}
        </p>
      </div>
    </div>

    <!-- RIGHT PANEL: Login Form -->
    <div
      class="flex-1 flex items-center justify-center p-6 sm:p-12 lg:p-16 bg-white dark:bg-gray-900"
    >
      <div class="w-full max-w-md">
        <!-- Title -->
        <div class="mb-8">
          <h1 class="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white">
            {{ t('provider_login.form.title') }}
          </h1>
          <p class="mt-2 text-sm text-gray-600 dark:text-gray-300">
            {{ t('provider_login.form.description') }}
          </p>
        </div>

        <form @submit.prevent="handleLogin" class="space-y-5">
          <!-- Email -->
          <div>
            <label
              for="loginEmail"
              class="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              {{ t('provider_login.form.email') }}
            </label>
            <input
              id="loginEmail"
              v-model="loginUsername"
              type="text"
              required
              class="w-full px-3 py-2 text-sm bg-gray-100 rounded-lg 
                     focus:outline-none focus:ring-2 focus:ring-primaryColor/30 
                     dark:bg-gray-800 dark:text-white"
              placeholder="doctor@clinic.com"
            />
          </div>

          <!-- Password -->
          <div>
            <label
              for="loginPassword"
              class="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              {{ t('provider_login.form.password') }}
            </label>
            <input
              id="loginPassword"
              v-model="loginPassword"
              type="password"
              required
              class="w-full px-3 py-2 text-sm bg-gray-100 rounded-lg 
                     focus:outline-none focus:ring-2 focus:ring-primaryColor/30
                     dark:bg-gray-800 dark:text-white"
              :placeholder="t('provider_login.form.password_placeholder')"
            />
          </div>

          <!-- Remember Me + Forgot Password -->
          <div class="flex items-center justify-between text-sm mt-2">
            <div class="flex items-center">
              <input
                id="remember"
                aria-describedby="remember"
                type="checkbox"
                v-model="loginRememberMe"
                class="w-4 h-4 border border-gray-300 rounded bg-gray-50 
                       focus:ring-3 focus:ring-primaryColor/50"
              />
              <label
                for="remember"
                class="ml-2 text-gray-700 dark:text-gray-200"
              >
                {{ t('provider_login.form.remember_me') }}
              </label>
            </div>

            <NuxtLink
              to="/provider/4/forget-password"
              class="text-blue-600 hover:underline dark:text-blue-400"
            >
              {{ t('provider_login.form.forgot_password') }}
            </NuxtLink>
          </div>

          <!-- Error Message -->
          <p v-if="loginError" class="text-sm text-red-500 mt-1">
            {{ loginError }}
          </p>

          <!-- Submit -->
          <div class="pt-4">
            <button
              type="submit"
              class="w-full px-4 py-2 text-sm font-medium text-white bg-primaryBtn 
                     rounded hover:bg-primaryBtnHover focus:outline-none 
                     focus:ring-2 focus:ring-primaryColor/50 disabled:bg-gray-300 
                     disabled:cursor-not-allowed"
              :disabled="submitting"
            >
              {{ submitting ? t('provider_login.form.logging_in') : t('provider_login.form.login') }}
            </button>
          </div>
        </form>

        <!-- Register Link -->
        <div class="flex items-center justify-center space-x-5 text-sm mt-4">
          <p class="text-gray-600 dark:text-gray-300">
            {{ t('provider_login.register.not_doctor') }}
            <NuxtLink
              :to="localePath('/')"
              class="text-blue-600 hover:underline dark:text-blue-400"
            >
              {{ t('provider_login.register.back_home') }}
            </NuxtLink>
          </p>
          <p class="text-gray-600 dark:text-gray-300">
            |
          </p>
          <p class="text-gray-600 dark:text-gray-300">
            {{ t('provider_login.register.not_registered') }}
            <NuxtLink
              :to="localePath('/provider/4/onboarding')"
              class="text-blue-600 hover:underline dark:text-blue-400"
            >
              {{ t('provider_login.register.create_account') }}
            </NuxtLink>
          </p>
        </div>

      </div>
    </div>

    <!-- TOAST / NOTIFICATION -->
    <ClientOnly>
      <transition name="fade">
        <div
          v-if="toastMessage"
          class="fixed top-4 right-4 z-50 bg-green-500 text-white px-4 py-2 rounded shadow-lg"
        >
          {{ toastMessage }}
        </div>
      </transition>
    </ClientOnly>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from '#imports'
import { useI18n } from 'vue-i18n'
import useAuth from '~/composables/useAuth'

definePageMeta({
  layout: 'app'
})

// i18n setup
const { t, locale: currentLocale } = useI18n()
const localePath = useLocalePath()

// Tinted BG
const leftPanelStyle = computed(() => ({
  backgroundImage: `linear-gradient(to bottom right, rgba(147,51,234,0.6), rgba(126,34,206,0.8)), url('/img/onboarding/login_backdrop.webp')`
}))

const router = useRouter()
const { loginUsername, loginPassword, loginRememberMe, doAuth, authStatus } = useAuth()

const submitting = ref(false)
const toastMessage = ref('')
const loginError = ref('')

onMounted(() => {
  // Already logged in? skip
  if (authStatus.value === 'authenticated') {
  }
})

function switchLanguage(event: Event) {
  const target = event.target as HTMLSelectElement
  const value = target.value as "en-US" | "zh-Hans" | "zh-Hant"
  currentLocale.value = value
  // Redirect to the localized version of the current page
  const currentPath = router.currentRoute.value.path
  router.push(localePath(currentPath))
}

async function handleLogin() {
  // Basic checks
  if (!loginUsername.value || !loginPassword.value) {
    loginError.value = t('provider_login.form.error.credentials_required')
    return
  }

  submitting.value = true
  loginError.value = ''
  toastMessage.value = ''

  try {
    await doAuth() // If no error => success
    // Show success message
    toastMessage.value = t('provider_login.form.success_message')
    setTimeout(() => {
      toastMessage.value = ''
      router.push(localePath('/provider/4/dashboard'))
    }, 1200)
  } catch (err: any) {
    console.error('[handleLogin] Error =>', err)
    // Display the Chinese error message from doAuth()
    loginError.value = err.message || t('provider_login.form.error.invalid_credentials')
  } finally {
    // wait for 10 seconds before allowing another login attempt
    setTimeout(() => {
      submitting.value = false
    }, 10000)
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 300ms ease-in-out;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
